import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Calendar as CalendarIcon,
  Trash2,
  Loader2,
  ShieldCheck,
  Zap,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Calendar/ui/dialog";
import { Button } from "@/components/Calendar/ui/button";
import { useTitanApp } from "@/hooks/useTitanApp";
import {
  getGoogleOAuthUrl,
  getGoogleCalendarStatus,
  getGoogleCalendars,
  triggerGoogleCalendarSync,
  disconnectGoogleCalendar,
  refreshScheduleEventsSilently,
  getScheduleEvents,
} from "@/dispatchers/scheduleEventsDispatchers";
import {
  showError,
  showSuccess,
  showInfo,
} from "@/components/ToastedMessage/ToastedMessage";

interface CalendarSyncModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pharmacyInfo?: {
    calendarSync?: {
      provider: "google" | "outlook" | null;
      email?: string;
      syncedAt?: string;
    };
  };
  isDarkMode: boolean;
  onSyncSuccess?: () => void;
}

export function CalendarSyncModal({
  open,
  onOpenChange,
  pharmacyInfo,
  isDarkMode,
  onSyncSuccess,
}: CalendarSyncModalProps) {
  const { appState, scheduleEventState } = useTitanApp();
  const { currentPharmacyId, currentCompanyId } = appState || {};
  const {
    googleCalendarStatus,
    isGoogleCalendarStatusLoading,
    isGoogleCalendarSyncing,
    isGoogleCalendarConnecting,
    isGoogleCalendarDisconnecting,
    googleCalendarsList = [],
  } = scheduleEventState || {};

  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [fullSync, setFullSync] = useState(false);
  const [syncGoogleToTitanverse, setSyncGoogleToTitanverse] = useState(false);
  const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const connectionHandledRef = useRef(false);

  // Determine if Google Calendar is connected
  const isGoogleConnected = Boolean(
    googleCalendarStatus?.isConnected ||
      googleCalendarStatus?.connected ||
      pharmacyInfo?.calendarSync?.provider === "google",
  );

  const googleEmail =
    googleCalendarStatus?.email ||
    googleCalendarStatus?.googleEmail ||
    pharmacyInfo?.calendarSync?.email ||
    "Connected Google Account";

  const lastSyncedAt =
    googleCalendarStatus?.lastSyncedAt ||
    googleCalendarStatus?.syncedAt ||
    pharmacyInfo?.calendarSync?.syncedAt;

  // Fetch status whenever modal is opened
  const fetchStatus = useCallback(async () => {
    try {
      const status = await getGoogleCalendarStatus();
      if (status?.isConnected || status?.connected) {
        await getGoogleCalendars();
      }
    } catch (err) {
      console.error("Failed to fetch Google Calendar status:", err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchStatus();
      setErrorMessage(null);
      setShowConfirmDisconnect(false);
    } else {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [open, fetchStatus]);

  // Handle OAuth Popup and polling
  const handleGoogleConnect = async () => {
    try {
      setIsConnecting(true);
      setErrorMessage(null);
      connectionHandledRef.current = false;

      const urlRes = await getGoogleOAuthUrl(currentPharmacyId, currentCompanyId);
      const authUrl = urlRes?.authorizationUrl || urlRes?.authUrl || urlRes;

      if (!authUrl || typeof authUrl !== "string") {
        throw new Error("Could not obtain Google authorization URL.");
      }

      // Calculate center coordinates for popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        "GoogleCalendarOAuth",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );

      popupRef.current = popup;

      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        // If popup blocked, redirect directly
        window.location.href = authUrl;
        return;
      }

      // Poll every 1.5s for popup closing or status change
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }

      pollTimerRef.current = setInterval(async () => {
        try {
          // setInterval fires a new async tick every 1.5s regardless of whether the previous
          // tick's awaits have resolved yet, so once the connection succeeds, several ticks can
          // already be in flight and each independently see isConnected=true before any of them
          // reaches clearInterval (which only stops *future* ticks, not ones already running).
          // Without this guard that fired triggerGoogleCalendarSync 2-3x. The check-and-set below
          // is synchronous (no await between them), so only the first tick to observe it can win.
          if (connectionHandledRef.current) {
            return;
          }

          const status = await getGoogleCalendarStatus();
          if (connectionHandledRef.current) {
            return;
          }

          if (status?.isConnected || status?.connected) {
            connectionHandledRef.current = true;
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            if (popupRef.current && !popupRef.current.closed) {
              popupRef.current.close();
            }
            setIsConnecting(false);
            showSuccess("Google Calendar connected successfully!");

            // Connecting only links the account - it must not pull any events into GoogleEvents
            // on its own. Syncing is a separate, explicit action (the "Sync Now" button below).
            if (currentPharmacyId) {
              await refreshScheduleEventsSilently(currentPharmacyId);
            }
            await getGoogleCalendars();
            onSyncSuccess?.();
            return;
          }

          if (popupRef.current?.closed) {
            connectionHandledRef.current = true;
            if (pollTimerRef.current) {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            setIsConnecting(false);
            // The OAuth popup closes itself once Google's callback has been processed (see the
            // backend's oauth/callback endpoint), so check status now to find out whether that
            // actually resulted in a connection or not, and toast accordingly either way.
            const finalCheck = await getGoogleCalendarStatus();
            if (finalCheck?.isConnected || finalCheck?.connected) {
              showSuccess("Google Calendar connected successfully!");
              // Connecting only links the account - no automatic sync into GoogleEvents here either.
              if (currentPharmacyId) {
                await refreshScheduleEventsSilently(currentPharmacyId);
              }
              await getGoogleCalendars();
              onSyncSuccess?.();
            } else {
              setErrorMessage("Google Calendar connection was not completed.");
              showError("Google Calendar connection was not completed.");
            }
          }
        } catch (e) {
          // ignore polling errors
        }
      }, 1500);
    } catch (err) {
      console.error("Google connect error:", err);
      setIsConnecting(false);
      setErrorMessage(err?.message || "Failed to initiate Google connection.");
      showError(err?.message || "Failed to initiate Google connection.");
    }
  };

  // The sync API kicks the actual sync off in the background and returns immediately
  // (a full/historical sync is too slow to wait on synchronously without timing out),
  // so poll the status endpoint's IsSyncing flag until the server reports it's done.
  const SYNC_POLL_INTERVAL_MS = 2000;
  const SYNC_POLL_TIMEOUT_MS = 5 * 60 * 1000;

  const waitForBackgroundSyncToFinish = async () => {
    const deadline = Date.now() + SYNC_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, SYNC_POLL_INTERVAL_MS));
      const status: any = await getGoogleCalendarStatus();
      if (!status?.isSyncing) {
        return;
      }
    }
  };

  // Handle Manual Trigger Sync
  const handleTriggerSync = async () => {
    try {
      setIsSyncing(true);
      setErrorMessage(null);

      await triggerGoogleCalendarSync(
        fullSync,
        currentPharmacyId,
        syncGoogleToTitanverse,
        currentCompanyId,
      );

      await waitForBackgroundSyncToFinish();

      // Refresh current schedule events
      if (currentPharmacyId) {
        await refreshScheduleEventsSilently(currentPharmacyId);
      }
      await fetchStatus();

      showSuccess(
        !syncGoogleToTitanverse
          ? "Appointments pushed to Google Calendar successfully!"
          : fullSync
          ? "Full two-way calendar sync completed successfully!"
          : "Calendar synchronized with Google successfully!",
      );
      onSyncSuccess?.();
    } catch (err) {
      console.error("Sync error:", err);
      setErrorMessage(err?.message || "Failed to sync calendar.");
      showError(err?.message || "Failed to sync calendar.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle Disconnect
  const handleGoogleDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      setErrorMessage(null);

      await disconnectGoogleCalendar();
      setShowConfirmDisconnect(false);
      await fetchStatus();

      showSuccess("Google Calendar disconnected.");
    } catch (err) {
      console.error("Disconnect error:", err);
      setErrorMessage(err?.message || "Failed to disconnect Google Calendar.");
      showError(err?.message || "Failed to disconnect Google Calendar.");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleOutlookConnect = () => {
    showInfo("Outlook Calendar sync integration is coming soon.");
  };

  const handleExportICS = () => {
    showInfo("Exporting appointments as an .ics file is in progress.");
  };

  const formatLastSynced = (dateStr?: string) => {
    if (!dateStr) return "Just now";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "Recently";
      return d.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-2xl max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-xl font-semibold flex items-center gap-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            <CalendarIcon className="w-5 h-5 text-blue-600" />
            Calendar Synchronization
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-3">
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Connect your external calendars for seamless real-time two-way
            synchronization. Appointments created, rescheduled, or cancelled in
            Clifton Care automatically sync to your calendar, and external
            events block out time.
          </p>

          {errorMessage && (
            <div
              className={`p-3.5 rounded-lg text-sm flex items-start gap-2.5 ${
                isDarkMode
                  ? "bg-red-900/30 border border-red-800 text-red-300"
                  : "bg-red-50 border border-red-200 text-red-700"
              }`}
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium">Sync Notice</p>
                <p className="text-xs opacity-90 mt-0.5">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Google Calendar Card */}
          <div
            className={`p-6 rounded-xl border transition-all ${
              isGoogleConnected
                ? isDarkMode
                  ? "bg-emerald-950/20 border-emerald-700/60 shadow-sm"
                  : "bg-emerald-50/70 border-emerald-300 shadow-sm"
                : isDarkMode
                ? "bg-gray-750/80 border-gray-700 hover:border-gray-600"
                : "bg-gray-50/90 border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                {/* Google Icon */}
                <div
                  className={`w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center shadow-sm ${
                    isDarkMode
                      ? "bg-gray-700 border border-gray-600"
                      : "bg-white border border-gray-150"
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </div>

                {/* Details */}
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3
                      className={`font-semibold text-lg ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Google Calendar
                    </h3>
                    {isGoogleConnected ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Disconnected
                      </span>
                    )}
                  </div>

                  {isGoogleConnected ? (
                    <div className="space-y-1">
                      <p
                        className={`text-sm font-medium ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Synced with{" "}
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                          {googleEmail}
                        </span>
                      </p>
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Last synchronized: {formatLastSynced(lastSyncedAt)}
                      </p>
                    </div>
                  ) : (
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Sync appointments bidirectionally with your Google
                      Calendar.
                    </p>
                  )}

                  {/* Feature Bullets */}
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    <div
                      className={`text-xs flex items-center gap-1.5 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      Two-way appointment sync
                    </div>
                    <div
                      className={`text-xs flex items-center gap-1.5 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      Instant create/edit/delete sync
                    </div>
                    <div
                      className={`text-xs flex items-center gap-1.5 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      Automatic block-out periods
                    </div>
                    <div
                      className={`text-xs flex items-center gap-1.5 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      Secure OAuth2 integration
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0 mt-3 sm:mt-0">
                {isGoogleConnected ? (
                  <>
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white gap-2 font-medium shadow-sm"
                      onClick={handleTriggerSync}
                      disabled={isSyncing || isGoogleCalendarSyncing}
                    >
                      <RefreshCw
                        className={`w-3.5 h-3.5 ${
                          isSyncing || isGoogleCalendarSyncing
                            ? "animate-spin"
                            : ""
                        }`}
                      />
                      {isSyncing || isGoogleCalendarSyncing
                        ? "Syncing..."
                        : "Sync Now"}
                    </Button>

                    {!showConfirmDisconnect ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className={`gap-1.5 text-xs ${
                          isDarkMode
                            ? "border-red-800/80 text-red-400 hover:bg-red-950/40"
                            : "border-red-200 text-red-600 hover:bg-red-50"
                        }`}
                        onClick={() => setShowConfirmDisconnect(true)}
                        disabled={
                          isDisconnecting || isGoogleCalendarDisconnecting
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Disconnect
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="destructive"
                          size="sm"
                          className="text-xs bg-red-600 hover:bg-red-700 text-white"
                          onClick={handleGoogleDisconnect}
                          disabled={
                            isDisconnecting || isGoogleCalendarDisconnecting
                          }
                        >
                          {isDisconnecting ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : null}
                          Confirm
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => setShowConfirmDisconnect(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white gap-2 font-medium shadow-sm"
                    onClick={handleGoogleConnect}
                    disabled={isConnecting || isGoogleCalendarConnecting}
                  >
                    {isConnecting || isGoogleCalendarConnecting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        Connect Google
                        <ExternalLink className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Sync options / Calendars list if connected */}
            {isGoogleConnected && (
              <div
                className={`mt-4 pt-4 border-t ${
                  isDarkMode ? "border-gray-700" : "border-emerald-200/80"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={fullSync}
                        onChange={(e) => setFullSync(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        Perform full historical synchronization (slower)
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={syncGoogleToTitanverse}
                        onChange={(e) =>
                          setSyncGoogleToTitanverse(e.target.checked)
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        Bring google appointment to Titanverse
                      </span>
                    </label>
                  </div>

                  {googleCalendarsList && googleCalendarsList.length > 0 && (
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {googleCalendarsList.length} calendar
                      {googleCalendarsList.length > 1 ? "s" : ""} available
                    </div>
                  )}
                </div>

                {/* Synced Calendars Pills */}
                {googleCalendarsList && googleCalendarsList.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {googleCalendarsList.map((cal: any, index: number) => (
                      <span
                        key={cal.id || index}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${
                          cal.primary || index === 0
                            ? isDarkMode
                              ? "bg-blue-900/30 border-blue-700 text-blue-200"
                              : "bg-blue-50 border-blue-200 text-blue-800"
                            : isDarkMode
                            ? "bg-gray-700 border-gray-600 text-gray-300"
                            : "bg-gray-100 border-gray-200 text-gray-700"
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{
                            backgroundColor:
                              cal.backgroundColor ||
                              (cal.primary ? "#4285F4" : "#10B981"),
                          }}
                        />
                        {cal.summary || cal.name || "Primary Calendar"}
                        {cal.primary && (
                          <span className="text-[10px] uppercase font-bold opacity-75">
                            (Primary)
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Outlook Calendar Card */}
          <div
            className={`hidden p-6 rounded-xl border opacity-85 transition-colors ${
              isDarkMode
                ? "bg-gray-750/70 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${
                    isDarkMode
                      ? "bg-gray-700 border border-gray-600"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path
                      fill="#0078D4"
                      d="M20.5 2h-17C2.67 2 2 2.67 2 3.5v17c0 .83.67 1.5 1.5 1.5h17c.83 0 1.5-.67 1.5-1.5v-17c0-.83-.67-1.5-1.5-1.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"
                    />
                    <path
                      fill="#FFF"
                      d="M12 8.5c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3
                      className={`font-semibold text-lg ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Outlook Calendar
                    </h3>
                    <span className="text-[11px] px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Sync appointments with Microsoft Outlook & Office 365
                    calendar.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="gap-1.5 text-xs opacity-60 cursor-not-allowed"
                onClick={handleOutlookConnect}
              >
                Connect
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Export ICS Options */}
          <div
            className={`hidden p-6 rounded-xl border ${
              isDarkMode
                ? "bg-gray-750/70 border-gray-700"
                : "bg-blue-50/50 border-blue-200/80"
            }`}
          >
            <h3
              className={`font-semibold mb-1 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Manual Export (.ics)
            </h3>
            <p
              className={`text-sm mb-4 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Download your schedule as an iCalendar (.ics) file compatible with
              Apple Calendar, Outlook, and Google Calendar.
            </p>
            <Button
              variant="outline"
              size="sm"
              className={`gap-2 ${
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
              onClick={handleExportICS}
            >
              <CalendarIcon className="w-4 h-4" />
              Download .ics File
            </Button>
          </div>
        </div>

        <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : ""
            }
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
